import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Loading } from 'components';
import { Button } from 'design-system-web';
import { H4 } from 'components/typography';
import { Alert } from 'components/lds';
import { ViewTenantLeases } from './ViewTenantLeases';
import { EditTenantLeases } from './EditTenantLeases';
import { useFetchPropertySuites } from '../hooks/useFetchPropertySuites';
import { useFetchTenantLeases } from '../hooks/useFetchTenantLeases';

type TenantLeasesSectionProps = {
  tenantId: string;
  propertyId: string;
};

export const TenantLeasesSection = ({
  tenantId,
  propertyId,
}: TenantLeasesSectionProps) => {
  const { t } = useTranslation();
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    loading: isLoadingSuites,
    suites,
    error: fetchPropertySuitesError,
  } = useFetchPropertySuites(propertyId);

  const {
    tenantLeases,
    loading: isLoadingLeases,
    error: fetchTenantLeasesError,
  } = useFetchTenantLeases(tenantId, propertyId);

  const isLoading = isLoadingSuites || isLoadingLeases;
  const hasError = fetchPropertySuitesError || fetchTenantLeasesError;

  return (
    <>
      <Flex direction="row" justify="space-between">
        <H4
          mb={4}
        >{t`web.admin.channel.settings.tenantManagement.tenant.details.tenantLeasesSection.title`}</H4>
        {!isEditMode && (
          <Button
            variant="secondary"
            testId="edit-tenant-leases"
            size="large"
            className="!px-2"
            onClick={() => setIsEditMode(true)}
          >
            {t(
              'web.admin.channel.settings.tenantManagement.tenant.details.tenantLeasesSection.edit'
            )}
          </Button>
        )}
      </Flex>
      {hasError && (
        <Flex direction="row" justify="flex-start">
          <Alert type="error">
            {t(
              'web.admin.channel.settings.tenantManagement.tenant.details.tenantLeasesSection.queryError'
            )}
          </Alert>
        </Flex>
      )}
      {isLoading && <Loading testId="loading-tenant-leases" />}
      {isEditMode && !isLoading && (
        <EditTenantLeases
          tenantId={tenantId}
          propertyId={propertyId}
          suites={suites}
          tenantLeases={tenantLeases}
          onCancel={() => setIsEditMode(false)}
          onSave={() => setIsEditMode(false)}
        />
      )}
      {!isEditMode && !isLoading && (
        <ViewTenantLeases tenantLeases={tenantLeases} />
      )}
    </>
  );
};
